import React, { useState, useEffect } from 'react';
import { Sparkles, Loader, AlertCircle, Download, TrendingUp, Activity, Zap, AlertTriangle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { D1_WORKER_URL } from '../services/api';

export default function NovaAI({ user }) {
  const [insights, setInsights] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);

  // Determine user scope based on role
  const isGlobalAdmin = ['super_admin', 'Superadmin', 'company_admin', 'Admin', 'regional', 'Regional', 'Regional Admin', 'electrician'].includes(user?.role);
  const hospitalId = user?.hospital_id || null;

  // Fetch dashboard data based on role
  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = isGlobalAdmin ? '/analytics/dashboard/company' : `/analytics/dashboard/hospital/${hospitalId}`;
        const response = await fetch(`${D1_WORKER_URL}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('portal_auth_token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setDashboardData(data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [isGlobalAdmin, hospitalId]);

  // Generate insights using Cloudflare AI
  const generateInsights = async () => {
    setLoading(true);
    setError('');

    try {
      // Build comprehensive data context from all available data
      let dataContext = 'No real-time data available. ';
      
      if (dashboardData) {
        const kpi = dashboardData.kpis || {};
        const successRate = kpi.total_sessions > 0 
          ? ((kpi.successful_sessions || 0) / kpi.total_sessions * 100).toFixed(1)
          : 0;
        
        // Format KPI section
        const kpiSection = isGlobalAdmin
          ? `COMPANY-WIDE KPIs:
- Total Robots: ${kpi.total_robots || 0} (Active in last 30 days: ${kpi.active_robots || 0})
- Total Hospitals: ${kpi.total_hospitals || 0}
- Total Sessions: ${kpi.total_sessions || 0}
- Successful Sessions: ${kpi.successful_sessions || 0}
- Failed Sessions: ${kpi.failed_sessions || 0}
- Success Rate: ${successRate}%
- Total Uptime: ${kpi.total_uptime_minutes ? (kpi.total_uptime_minutes / 60).toFixed(1) : 0} hours`
          : `HOSPITAL KPIs (${user?.hospital_name}):
- Total Robots: ${kpi.total_robots || 0} (Active in last 30 days: ${kpi.active_robots || 0})
- Total Sessions: ${kpi.total_sessions || 0}
- Successful Sessions: ${kpi.successful_sessions || 0}
- Failed Sessions: ${kpi.failed_sessions || 0}
- Success Rate: ${successRate}%
- Total Uptime: ${kpi.total_uptime_minutes ? (kpi.total_uptime_minutes / 60).toFixed(1) : 0} hours`;

        // Format trends section
        let trendsSection = '';
        if (dashboardData.trends && dashboardData.trends.length > 0) {
          trendsSection = `\n\nRECENT TRENDS (Last 7 Days):`;
          dashboardData.trends.forEach(trend => {
            trendsSection += `\n- ${trend.date}: ${trend.sessions} sessions, ${trend.successful || 0} successful, ${trend.failed || 0} failed, ${(trend.total_minutes || 0) / 60} hours uptime`;
          });
        }

        // Format robots/operators section
        let robotsSection = '';
        if (dashboardData.robots && dashboardData.robots.length > 0) {
          const title = isGlobalAdmin ? 'TOP HOSPITALS:' : 'TOP WARDS:';
          const nameField = isGlobalAdmin ? 'hospital_name' : 'ward_name';
          robotsSection = `\n\n${title}`;
          dashboardData.robots.slice(0, 5).forEach(robot => {
            // Skip entries with missing or invalid names
            const name = robot[nameField]?.trim();
            if (!name || name === 'undefined' || name === 'null' || name === 'Unknown Hospital' || name === 'Unknown Ward') {
              return; // Skip this entry
            }
            const killSwitches = robot.kill_switch_stops || 0;
            const otherErrors = robot.error_failures || 0;
            let failureDetail = '';
            if (killSwitches > 0 || otherErrors > 0) {
              failureDetail = ` (${killSwitches} kill switch stops, ${otherErrors} other errors)`;
            }
            robotsSection += `\n- ${name}: ${robot.session_count} sessions, ${robot.successful_sessions || 0} successful, ${robot.failed_sessions || 0} failed${failureDetail}, Last active: ${robot.last_session}`;
          });
        }

        dataContext = kpiSection + trendsSection + robotsSection;
      }

      const scopeLabel = isGlobalAdmin ? 'company-wide' : `${user?.hospital_name} hospital`;
      
      const prompt = `You are an AI executive assistant for the EveSix UV-C disinfection robot management system. Analyze the following ${scopeLabel} fleet data and provide an executive summary focusing on operational trends, hospital/ward patterns, and performance issues.

FAILURE TYPE DEFINITIONS:
- Kill Switch Stops (error_code = '100'): Sessions intentionally terminated by staff (expected behavior)
- Other Errors: Software glitches, hardware failures, system errors (unexpected)

${dataContext}

Please provide a comprehensive executive summary with:

1. **Fleet Health Overview** (2-3 sentences): Current operational status, success rate analysis, and overall fleet performance trend

2. **Usage Patterns & Trends**: 
   - Session volume trends over the past week
   - Peak usage patterns by hospital/ward
   - Utilization rate assessment

3. **Performance Analysis**:
   - Top performing hospitals/wards
   - Fault patterns: differentiate between intentional kill switch stops vs actual system errors
   - Success rate trends

4. **Operational Issues & Alerts**:
   - Critical system failures (excluding kill switch stops) by hospital/ward
   - Under-utilized resources
   - Unexpected error spikes that need investigation

5. **Recommendations**:
   - Immediate actions for actual failures
   - Optimization opportunities by hospital/ward
   - Maintenance recommendations

Important: When referencing failures, specify whether they are kill switch stops (normal/intentional) or actual system errors. Only flag actual system errors as concerns. Keep the analysis data-driven and actionable.`;

      const response = await fetch(`${D1_WORKER_URL}/ai/insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('portal_auth_token')}`
        },
        body: JSON.stringify({
          prompt,
          model: '@cf/zai-org/glm-4.7-flash'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Failed to generate insights');
      }

      const data = await response.json();
      setInsights(data.insights || data.result || 'Unable to generate insights');
    } catch (err) {
      setError(err.message || 'Failed to generate insights');
      console.error('Error generating insights:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl border border-blue-200">
                <Sparkles className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">Nova AI</h1>
                <p className="text-slate-600 text-sm mt-2 max-w-xl">AI-powered executive insights for your fleet operations. Get data-driven recommendations tailored to your role.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Input Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Role Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Data Scope</h3>
              <div className="flex items-center gap-3">
                <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  isGlobalAdmin 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {user?.role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </div>
                <p className="text-slate-600 text-sm">
                  {isGlobalAdmin 
                    ? 'Company-wide analytics' 
                    : `${user?.hospital_name || 'Hospital'} operations`}
                </p>
              </div>
            </div>

            {/* Generate Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Generate Insights</h3>
              
              {dashboardData && (
                <p className="text-sm text-emerald-600 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Fleet data loaded
                </p>
              )}
              
              {!dashboardData && !loading && (
                <p className="text-sm text-slate-600 mb-4">Fleet data syncing (optional)</p>
              )}
              
              <button
                onClick={generateInsights}
                disabled={loading}
                className={`w-full px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  loading
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-500/20 hover:shadow-blue-500/30'
                }`}
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Generating Insights...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>Generate AI Insights</span>
                  </>
                )}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-900 font-medium text-sm">Error generating insights</p>
                  <p className="text-red-700 text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Insights Display */}
            {insights && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-100 rounded-lg">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Executive Summary</h2>
                      <p className="text-xs text-slate-500 mt-1">AI-Generated Analysis</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-slate-500 mb-1">Generated</p>
                    <span className="inline-block text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                      {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-8 border border-slate-200 mb-8 space-y-4">
                  {/* Parse and format the insights */}
                  {insights.split('\n\n').map((paragraph, idx) => {
                    const trimmed = paragraph.trim();
                    if (!trimmed) return null;
                    
                    // Section mapping for icons
                    const sectionIcons = {
                      'Fleet Health': <Activity className="w-5 h-5" />,
                      'Usage Patterns': <TrendingUp className="w-5 h-5" />,
                      'Performance': <Zap className="w-5 h-5" />,
                      'Operational Issues': <AlertTriangle className="w-5 h-5" />,
                      'Recommendations': <Sparkles className="w-5 h-5" />
                    };
                    
                    // Check if it's a section header (bold text followed by content)
                    if (trimmed.includes('**') || /^[\d]+\./.test(trimmed)) {
                      return (
                        <div key={idx} className="space-y-2">
                          {trimmed.split('\n').map((line, lineIdx) => {
                            const cleaned = line.replace(/\*\*/g, '').trim();
                            if (!cleaned) return null;
                            
                            if (/^[\d]+\./.test(cleaned)) {
                              // Section header
                              const sectionTitle = cleaned.substring(2).replace(/:.*/g, '').trim();
                              let sectionIcon = null;
                              for (const [key, icon] of Object.entries(sectionIcons)) {
                                if (sectionTitle.includes(key)) {
                                  sectionIcon = icon;
                                  break;
                                }
                              }
                              
                              return (
                                <h3 key={lineIdx} className="font-semibold text-slate-900 text-base mt-5 mb-3 flex items-center gap-2 pt-2 border-t border-blue-200">
                                  <span className={`flex items-center justify-center w-7 h-7 ${
                                    idx % 2 === 0 ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                                  } rounded-lg`}>
                                    {sectionIcon}
                                  </span>
                                  <span>{sectionTitle}</span>
                                </h3>
                              );
                            } else if (cleaned.startsWith('-')) {
                              // Bullet point
                              return (
                                <div key={lineIdx} className="flex gap-3 ml-10">
                                  <span className="text-blue-600 flex-shrink-0 mt-0.5 font-bold">◆</span>
                                  <p className="text-slate-700 text-sm leading-relaxed">{cleaned.substring(1).trim()}</p>
                                </div>
                              );
                            } else {
                              // Regular text
                              return (
                                <p key={lineIdx} className="text-slate-700 text-sm leading-relaxed ml-10">
                                  {cleaned}
                                </p>
                              );
                            }
                          })}
                        </div>
                      );
                    } else {
                      // Regular paragraph
                      return (
                        <p key={idx} className="text-slate-700 text-sm leading-relaxed ml-10 font-medium">
                          {trimmed}
                        </p>
                      );
                    }
                  })}
                </div>

                {/* Export Section */}
                <div className="bg-slate-50 rounded-lg p-6 border border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Export Analysis</h3>
                    <p className="text-xs text-slate-600 mt-1">Download as professional PDF report</p>
                  </div>
                  <button
                    onClick={() => {
                      const pdf = new jsPDF({
                        orientation: 'portrait',
                        unit: 'mm',
                        format: 'a4'
                      });
                      
                      const margin = 15;
                      const pageWidth = pdf.internal.pageSize.getWidth();
                      const pageHeight = pdf.internal.pageSize.getHeight();
                      const contentWidth = pageWidth - 2 * margin;
                      let yPos = margin;
                      
                      // Helper function to add text with auto page break
                      const addText = (text, fontSize = 11, options = {}) => {
                      if (yPos > pageHeight - margin - 10) {
                        pdf.addPage();
                        yPos = margin;
                      }
                      
                      pdf.setFontSize(fontSize);
                      const lines = pdf.splitTextToSize(text, contentWidth);
                      
                      lines.forEach((line) => {
                        if (yPos > pageHeight - margin) {
                          pdf.addPage();
                          yPos = margin;
                        }
                        pdf.text(line, margin, yPos);
                        yPos += options.spacing || 5;
                      });
                      
                      return yPos;
                    };
                    
                    // Title
                    pdf.setFont(undefined, 'bold');
                    pdf.setFontSize(18);
                    pdf.setTextColor(30, 41, 59);
                    yPos = addText('EveSix Nova AI Executive Summary', 18, { spacing: 8 });
                    
                    // Metadata
                    pdf.setFont(undefined, 'normal');
                    pdf.setFontSize(10);
                    pdf.setTextColor(100, 116, 139);
                    yPos += 2;
                    yPos = addText(`Generated: ${new Date().toLocaleString()}`, 10, { spacing: 4 });
                    yPos = addText(`Scope: ${isGlobalAdmin ? 'Company-wide Fleet' : user?.hospital_name || 'Hospital'}`, 10, { spacing: 4 });
                    yPos = addText(`User Role: ${user?.role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`, 10, { spacing: 8 });
                    
                    // Divider
                    pdf.setDrawColor(200, 213, 222);
                    pdf.line(margin, yPos, pageWidth - margin, yPos);
                    yPos += 6;
                    
                    // Content - properly parse and format
                    pdf.setTextColor(30, 41, 59);
                    const paragraphs = insights.split('\n\n').filter(p => p.trim());
                    
                    paragraphs.forEach((paragraph) => {
                      const lines = paragraph.split('\n');
                      
                      lines.forEach((line, idx) => {
                        const trimmed = line.trim();
                        if (!trimmed) return;
                        
                        if (yPos > pageHeight - margin - 5) {
                          pdf.addPage();
                          yPos = margin;
                        }
                        
                        // Section headers (numbered or bolded)
                        if (/^\d+\.|^#+\s|^\*\*/.test(trimmed)) {
                          pdf.setFont(undefined, 'bold');
                          pdf.setFontSize(12);
                          pdf.setTextColor(30, 41, 59);
                          const cleanedHeader = trimmed.replace(/^\d+\.\s*/, '').replace(/^#+\s*/, '').replace(/\*\*/g, '');
                          const headerLines = pdf.splitTextToSize(cleanedHeader, contentWidth);
                          headerLines.forEach((hLine) => {
                            pdf.text(hLine, margin, yPos);
                            yPos += 6;
                          });
                          yPos += 2;
                        }
                        // Bullet points
                        else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                          pdf.setFont(undefined, 'normal');
                          pdf.setFontSize(10);
                          pdf.setTextColor(71, 85, 105);
                          const bulletText = trimmed.substring(2);
                          const bulletLines = pdf.splitTextToSize(bulletText, contentWidth - 5);
                          
                          pdf.text('•', margin + 2, yPos);
                          bulletLines.forEach((bLine, bIdx) => {
                            if (bIdx === 0) {
                              pdf.text(bLine, margin + 8, yPos);
                            } else {
                              yPos += 5;
                              pdf.text(bLine, margin + 8, yPos);
                            }
                          });
                          yPos += 5;
                        }
                        // Regular text
                        else {
                          pdf.setFont(undefined, 'normal');
                          pdf.setFontSize(10);
                          pdf.setTextColor(51, 65, 85);
                          const textLines = pdf.splitTextToSize(trimmed, contentWidth);
                          textLines.forEach((tLine) => {
                            if (yPos > pageHeight - margin) {
                              pdf.addPage();
                              yPos = margin;
                            }
                            pdf.text(tLine, margin, yPos);
                            yPos += 5;
                          });
                          yPos += 1;
                        }
                      });
                      
                      yPos += 3; // Space between paragraphs
                    });
                    
                    // Footer with page numbers
                    const pageCount = pdf.getNumberOfPages();
                    for (let i = 1; i <= pageCount; i++) {
                      pdf.setPage(i);
                      pdf.setFontSize(8);
                      pdf.setTextColor(150, 162, 175);
                      pdf.text(
                        `Page ${i} of ${pageCount}`,
                        pageWidth / 2,
                        pageHeight - 8,
                        { align: 'center' }
                      );
                    }
                    
                    pdf.save(`nova-ai-insights-${new Date().toISOString().split('T')[0]}.pdf`);
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md"
                  >
                    <Download className="w-4 h-4" />
                    Export PDF
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Info Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* KPI Cards */}
            {dashboardData && dashboardData.kpis && (
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <Activity className="w-4 h-4 text-blue-600" />
                  </div>
                  Key Metrics
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">Total Sessions</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{dashboardData.kpis.total_sessions?.toLocaleString() || 0}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">Success Rate</p>
                    <p className="text-xl font-bold text-emerald-600 mt-1">
                      {dashboardData.kpis.total_sessions > 0 
                        ? ((dashboardData.kpis.successful_sessions || 0) / dashboardData.kpis.total_sessions * 100).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">Successful</p>
                    <p className="text-xl font-bold text-blue-600 mt-1">{dashboardData.kpis.successful_sessions?.toLocaleString() || 0}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">Failed</p>
                    <p className="text-xl font-bold text-red-600 mt-1">{dashboardData.kpis.failed_sessions?.toLocaleString() || 0}</p>
                  </div>
                  
                  {dashboardData.kpis.total_robots && (
                    <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                      <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">Total Robots</p>
                      <div className="mt-1">
                        <p className="text-lg font-bold text-purple-600">{dashboardData.kpis.total_robots}</p>
                        <p className="text-xs text-slate-500 mt-1">Active (30d): {dashboardData.kpis.active_robots || 0}</p>
                      </div>
                    </div>
                  )}
                  
                  {dashboardData.kpis.total_hospitals && (
                    <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow">
                      <p className="text-xs text-slate-600 uppercase tracking-wide font-medium">Hospitals</p>
                      <p className="text-xl font-bold text-indigo-600 mt-1">{dashboardData.kpis.total_hospitals}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Info Box */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm sticky top-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                How It Works
              </h3>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 font-semibold flex-shrink-0">1</span>
                  <span className="text-sm text-slate-600">Analyzes fleet performance data based on your role</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 font-semibold flex-shrink-0">2</span>
                  <span className="text-sm text-slate-600">Generates executive-level insights and recommendations</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 font-semibold flex-shrink-0">3</span>
                  <span className="text-sm text-slate-600">Identifies risks and opportunities automatically</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 font-semibold flex-shrink-0">4</span>
                  <span className="text-sm text-slate-600">Scoped to {isGlobalAdmin ? 'company-wide' : 'hospital'} operations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
